import { useState } from 'react';
import { Eye, EyeOff, Loader2, Lock, Mail, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email.trim(), password);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al iniciar sesión';
      if (errorMessage.includes('Invalid login credentials')) {
        setError('Credenciales inválidas. Verifica tu email y contraseña.');
      } else if (errorMessage.includes('Email not confirmed')) {
        setError('Debes confirmar tu email antes de iniciar sesión.');
      } else if (errorMessage.includes('rate limit')) {
        setError('Demasiados intentos. Espera unos minutos e inténtalo nuevamente.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
      <div className="orb orb-lime w-[520px] h-[520px] -top-32 -left-32" aria-hidden="true" />
      <div
        className="orb orb-violet w-[480px] h-[480px] -bottom-32 -right-32"
        style={{ animationDelay: '4s' }}
        aria-hidden="true"
      />
      <div
        className="orb orb-cyan w-[380px] h-[380px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30"
        style={{ animationDelay: '8s' }}
        aria-hidden="true"
      />

      <div className="relative-z w-full max-w-md animate-slide-up">
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-5">
            <div className="absolute inset-0 bg-cubus-lime/20 blur-2xl rounded-full scale-150" />
            <img
              src="/logo_login.svg"
              alt="Cubus"
              width={192}
              height={64}
              style={{ width: 192, height: 'auto', position: 'relative' }}
            />
          </div>
          <div className="flex items-center gap-2 chip chip-lime mb-5">
            <Sparkles className="w-3 h-3" aria-hidden="true" />
            <span>Plataforma Educativa</span>
          </div>
          <h1 className="text-display text-4xl font-bold text-white mb-2 tracking-tight text-center">
            Bienvenido de vuelta
          </h1>
          <p className="text-white/60 text-center">Ingresa tus credenciales para continuar</p>
        </div>

        <div className="glass-elevated rounded-3xl p-8 border-gradient">
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                Correo electrónico
              </label>
              <div className="relative group">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none group-focus-within:text-cubus-lime transition-colors"
                  aria-hidden="true"
                />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-invalid={error ? 'true' : 'false'}
                  aria-describedby={error ? 'login-error' : undefined}
                  className="glass-input w-full pl-11 pr-4 py-3.5 rounded-xl text-white placeholder-white/30"
                  placeholder="tu@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                Contraseña
              </label>
              <div className="relative group">
                <Lock
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none group-focus-within:text-cubus-lime transition-colors"
                  aria-hidden="true"
                />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-invalid={error ? 'true' : 'false'}
                  aria-describedby={error ? 'login-error' : undefined}
                  className="glass-input w-full pl-11 pr-11 py-3.5 rounded-xl text-white placeholder-white/30"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-white rounded-lg focus-lime transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                id="login-error"
                role="alert"
                className="p-3.5 bg-red-500/10 border border-red-500/30 backdrop-blur-sm rounded-xl text-sm animate-fade-in"
              >
                <p className="text-red-300 font-medium">{error}</p>
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-lime w-full py-3.5 text-[15px]">
              {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              <span>{loading ? 'Ingresando…' : 'Iniciar Sesión'}</span>
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-white/40 mt-6">
          © {new Date().getFullYear()} Cubus · Plataforma educativa interna
        </p>
      </div>
    </div>
  );
}
